"use client";

import type React from "react";
import type { ConsumerProps, FC, NamedExoticComponent, PropsWithChildren } from "react";
import { createNamedConsumer } from "./createNamedConsumer";
import { createNamedContext } from "./createNamedContext";
import { createNamedContextHook } from "./createNamedContextHook";
import { createNamedProvider, type PropsAreEqualFn } from "./createNamedProvider";

type HookName<T, N extends string> = { [P in `use${N}`]: () => T };
type ProviderName<N extends string, P> = { [T in `${N}Provider`]: FC<PropsWithChildren<P>> };
type ConsumerName<N extends string, R> = { [T in `${N}Consumer`]: NamedExoticComponent<ConsumerProps<R>> };
type ContextName<N extends string, R> = { [T in `${N}Context`]: React.Context<R> };

type HookContext<N extends string, P, R> = [
  () => R,
  FC<PropsWithChildren<P>>,
  NamedExoticComponent<ConsumerProps<R>>,
  React.Context<R | undefined>,
] &
  HookName<R, N> &
  ProviderName<N, P> &
  ConsumerName<N, R> &
  ContextName<N, R | undefined>;

export function createHookContext<N extends string, P extends object, R>(
  name: N,
  useHook: (props: PropsWithChildren<P>) => R,
  propsAreEqual?: PropsAreEqualFn<P>,
): HookContext<N, PropsWithChildren<P>, R> {
  const hookName = `use${name}`;
  const providerName = `${name}Provider`;
  const consumerName = `${name}Consumer`;
  const contextName = `${name}Context`;

  const Context = createNamedContext<ReturnType<typeof useHook>>(contextName);

  const useContext = createNamedContextHook(hookName, Context);

  const Provider = createNamedProvider<P, R | undefined>(Context, useHook, propsAreEqual);
  Provider.displayName = providerName;

  const Consumer = createNamedConsumer(consumerName, useContext);

  return Object.assign([useContext, Provider, Consumer, Context], {
    [hookName]: useContext,
    [providerName]: Provider,
    [consumerName]: Consumer,
    [contextName]: Context,
  }) as HookContext<N, P, ReturnType<typeof useHook>>;
}
